<template>
  <error-page v-if="isNotFound"
              :error="{ statusCode: 404 }" />
  <div v-else
       id="products"
       v-resize="handleResize">
    <lazy-hydrate never>
      <transition name="fade">
        <products-sale-banner v-if="showSaleBanner"
                              :saleBanner="saleBanner" />
      </transition>
    </lazy-hydrate>

    <lazy-hydrate never>
      <collection-header v-if="collection"
                         :collection="collectionHeader"/>
    </lazy-hydrate>

    <div class="grid-wrapper products">
      <default-error v-if="error"/>
      <ais-instant-search v-if="searchProducts"
                          :key="`${$route.fullPath}-${instantSearchRenderKey}`"
                          v-bind="instantSearchParams"
                          class="product-list"
                          :data-insights-product-index="indexName"
                          :middlewares="instantSearchMiddlewares">
        <ais-configure :hitsPerPage="60"
                       :filters="defaultFilters"
                       :disjunctiveFacets="disjunctiveFacets"
                       :facetingAfterDistinct="facetingAfterDistinct"
                       :enableRules="true"
                       :ruleContexts="['all']" />

        <products-listing-breadcrumbs v-if="category"
                                      :breadcrumbs="createBreadcrumbs(category.permalink)"/>
        <br v-else>

        <component v-if="isCategoriesCarouselVisible"
                   :is="categoriesCarouselComponent"
                   :items="category.childrens"
                   :title="mobile && productsListingHeaderCategory.name"
                   class="grid-wrapper products-listing-categories-carousel"/>

        <products-listing-header v-if="!mobile || !!productsListingHeaderCategory.name"
                                 :category="productsListingHeaderCategory"
                                 :categories="categories"
                                 :index-name="indexName"
                                 :is-title-visible="!isCategoriesCarouselVisible || !mobile"
                                 class="products-listing-header"/>

        <div class="product-list-main">
          <div id="filter-options-wrapper">
            <sticky-top-bar parent-container-selector=".grid-wrapper.products"
                            :offset="filtersOffset"
                            :active="!mobile"
                            :z-index="15">
              <filter-options :show-mobile-title="!isProductsPage"
                              :filters="filters"
                              :alternative-filters="alternativeFilters"
                              :index-name="indexName"
                              :ui-data="uiData"
                              :categories="categories"
                              @resize="computeFiltersTop"
                              @applyFilters="applyFilters"
                              @hasFeatureFilterEvent="setHasFeatureFilterState" />
            </sticky-top-bar>
          </div>
          <product-list :products-loading="productsLoading"
                        :enable-edit-ability="enableProductEditAbility"
                        @productsChanged="triggerFunctionsAfterMounting"/>
        </div>
      </ais-instant-search>
    </div>

    <lazy-hydrate never>
      <product-footer v-if="category"
                      :description="category.long_description"/>
    </lazy-hydrate>

    <lazy-hydrate never>
      <product-footer v-if="collection"
                      :description="collection.product_footer_description"/>
    </lazy-hydrate>
  </div>
</template>

<script>
  import ApiProduct from '@/api/Product'
  import ApiSlide from '@/api/Slide'

  import { countBy, map, isEmpty, compact } from 'lodash'
  import { createBreadcrumbs } from '@/../shared/utils/utils'

  import { AisInstantSearch, AisConfigure, AisDynamicWidgets } from 'vue-instantsearch'
  import CategoryHeader from '@/components/CategoryHeader'
  import DefaultError from '@/components/DefaultError'
  import ErrorPage from '@/layouts/error'
  import FilterOptions from '@/components/FilterOptions'
  import LazyHydrate from 'vue-lazy-hydration'
  import Loader from '@/components/Loader'
  import Pagination from '@/../shared/components/Pagination'
  import ProductFooter from '@/components/ProductFooter'
  import ProductList from '@/components/ProductList'
  import ProductsListingBreadcrumbs from '@/components/products/listing/ProductsListingBreadcrumbs'
  import ProductsListingHeader from '@/components/products/listing/ProductsListingHeader'
  import ProductsSaleBanner from '@/components/products/ProductsSaleBanner'
  import SaleHeader from '@/components/SaleHeader'
  import StickyTopBar from '@/../shared/components/StickyTopBar'

  import AlgoliaInsightsMiddelwareMixin from '@/mixins/algoliaInsightsMiddleware'
  import AlgoliaProductsInstantSearchMixin from '@/mixins/algoliaProductsInstantSearch'
  import AlgoliaProductsRoutingMixin from '@/mixins/algoliaProductsRouting'
  import AlternativeFiltersMixin from '@/mixins/alternativeFilters'
  import CordialMixin from '@/mixins/cordial'
  import DynamicProductCategoryMixin from '@/mixins/dynamicProductCategory'
  import ProductsFiltersLoadableMixin from '@/mixins/productsFiltersLoadable'
  import ProductsCategoriesLoadableMixin from '@/mixins/productsCategoriesLoadable'
  import ProductsListingResponsive from '@/mixins/products/listing/productsListingResponsive'
  import ProductsListMetadata from '@/mixins/productsListMetadata'
  import StickyFiltersMixin from '@/mixins/stickySideFilters'

  export default {
    name: 'products-view',
    mixins: [
      AlgoliaInsightsMiddelwareMixin,
      AlgoliaProductsInstantSearchMixin,
      AlgoliaProductsRoutingMixin,
      AlternativeFiltersMixin,
      CordialMixin,
      ProductsCategoriesLoadableMixin,
      // "ProductsFiltersLoadableMixin" mixin must be declared before "DynamicProductCategoryMixin"
      // because it overrides the calculated property "isDynamicCategoryPage"
      ProductsFiltersLoadableMixin,
      DynamicProductCategoryMixin,
      ProductsListingResponsive,
      ProductsListMetadata,
      StickyFiltersMixin
    ],
    components: {
      AisDynamicWidgets,
      AisConfigure,
      AisInstantSearch,
      CategoriesCarouselDesktop: () => import('@/components/categories/CategoriesCarouselDesktop'),
      CategoriesCarouselMobile: () => import('@/components/categories/CategoriesCarouselMobile'),
      CategoryHeader,
      CollectionHeader: () => import('@/components/collections/CollectionHeader'),
      DefaultError,
      ErrorPage,
      FilterOptions,
      LazyHydrate,
      Loader,
      Pagination,
      ProductFooter,
      ProductList,
      ProductsListingBreadcrumbs,
      ProductsListingHeader,
      ProductsSaleBanner,
      SaleHeader,
      StickyTopBar,
    },
    head () {
      return {
        title: this.getMetaTitle,
        link: [
          {
            rel: 'canonical',
            href: `${this.$config.baseUrl}${this.$route.path}`
          }
        ],
        meta: [
          {
            name: 'description',
            content: this.getMetaDescription
          }
        ],
        script: [
          {
            json: {
              '@context': 'http://schema.org',
              '@type': 'ItemList',
              'itemListElement': this.metadataProducts
            },
            type: 'application/ld+json'
          }
        ]
      }
    },
    data () {
      return {
        error: false,
        category: null,
        collection: null,
        saleBanner: null,
        hasFeatureFilter: false,
        productsLoading: false,
        productsMeta: {
          seo_title: 'Products',
          seo_description: 'List of high quality products from several amazing brands such as Four Hands, Selamat Designs, Noir and more',
        },
        productsLoaded: false,
        isNotFound: false,
        unsubscribeMutationListener: null
      }
    },
    async fetch () {
      await Promise.all([
        this.subscribeSetSearchReload(),
        this.$store.dispatch('productData/clearCurrentProduct'),
        this.loadProducts(this.$route.fullPath),
        ...this.isProductsPage ? [this.loadSaleBanner()] : []
      ])
    },
    mounted () {
      this.loadFilters()
      this.loadCategories()
    },
    beforeDestroy () {
      if (typeof this.unsubscribeMutationListener === 'function') this.unsubscribeMutationListener()
    },
    computed: {
      searchProducts () {
        let searchProducts = this.filtersLoaded && this.productsLoaded

        if (this.$route.name === 'collections-slug') {
          searchProducts = searchProducts && !isEmpty(this.collection)
        }

        return searchProducts
      },
      enableProductEditAbility () {
        return this.$route.name === 'categories'
      },
      showSaleBanner () {
        return this.$route.name === 'products' &&
          this.saleBanner &&
          Object.keys(this.$route.query?.search || {}).length === 1 &&
          Array.isArray(this.$route.query.search.sale) &&
          this.$route.query.search.sale.length > 0 &&
          this.$route.query.search.sale[0] === 'true'
      },
      getMetaTitle () {
        let metaTitle = null

        if (this.category) {
          metaTitle = this.category.seo_title
        } else if (this.collection) {
          metaTitle = this.collection.seo_title
        } else {
          metaTitle = this.productsMeta.seo_title
        }

        return (this.isDynamicCategoryPage ? this.getDynamicCategoryPageTitle : metaTitle) || ''
      },
      getDynamicCategoryPageTitle () {
        if (!this.collection) return

        return this.getDynamicCollectionCategoryPageTitle
      },
      getDynamicCollectionCategoryPageTitle () {
        return `Shop ${this.collection?.name} ${this.dynamicCategoryName} Styles | Scout & Nimble`
      },
      getDynamicCategoryPageDescription () {
        if (!this.collection) return

        return this.getDynamicCollectionCategoryPageDescription
      },
      getDynamicCollectionCategoryPageDescription () {
        return `Transform your space and refine elegance with ${this.collection?.name} ` +
          `${this.dynamicCategoryName}. Shop top designers with an extensive range of ` +
          `${this.dynamicCategoryName} with ${this.collection?.name} style.`
      },
      getMetaDescription () {
        let metaDescription = null

        if (this.category) {
          metaDescription = this.category.seo_description || this.category.short_description
        } else if (this.collection) {
          metaDescription = this.collection.seo_description
        } else {
          metaDescription = this.productsMeta.seo_description
        }

        return (this.isDynamicCategoryPage ? this.getDynamicCategoryPageDescription : metaDescription) || ''
      },
      isProductsPage () {
        return this.$route.name === 'products'
      },
      isSalePage () {
        return !!this.$route.query.search?.sale
      },
      defaultFilters () {
        switch (this.$route.name) {
          case 'categories':
            let categoryLvl = countBy(this.slug, e => e === '/').true || 0
            let categoryValue = this.slug.replace(/\//g, ' > ')

            return `category.lvl${categoryLvl}:"${categoryValue}"`
          case 'collections-slug':
          case 'collections-slug-category':
            return compact([
              this.categoryDefaultFilters,
              this.collectionDefaultFilters
            ]).join(' AND ')
          default:
            return ''
        }
      },
      collectionDefaultFilters () {
        const values = map(this.collectionFilters, this.collectionFilterDefaultValues)
        const valuesAlternatives = map(values, value => value.length ? `(${value.join(' OR ')})` : null)

        return compact(valuesAlternatives).join(' AND ')
      },
      collectionFilters () {
        return this.collection?.collection_filters || {}
      },
      slug () {
        return this.$route.params.name
      },
      collectionHeader () {
        const { product_title, product_description, product_footer_description, name } = this.collection
        const sentenceMatch = product_footer_description?.match(/^[^.!?]*[.!?]/)
        const sentence = sentenceMatch ? sentenceMatch[0] : product_description

        return {
          ...this.collection,
          product_title: this.isDynamicCategoryPage ? `${name} ${this.dynamicCategoryName}` : product_title,
          product_description: this.isDynamicCategoryPage ? sentence : product_description
        }
      },
      productsListingHeaderCategory () {
        if (this.isProductsPage && !this.isSalePage && !this.hasFeatureFilter) {
          return { name: 'All Products' }
        }
        if (this.category) {
          return this.category
        }

        return {}
      },
      isCategoriesCarouselVisible () {
        return !!this.category?.childrens?.length
      },
      categoriesCarouselComponent () {
        return this.windowWidth < 480 ? 'CategoriesCarouselMobile' : 'CategoriesCarouselDesktop'
      }
    },
    methods: {
      createBreadcrumbs,
      loadProducts(path) {
        this.productsLoading = process.client

        const config = {}
        const params = { category_permalink: this.categoryPermalink }

        return ApiProduct.getProducts(this.$axios, this.getApiPath(path), config, params)
          .then(response => {
            this.category = response.data.category
            this.collection = response.data.collection
            this.productsMeta = response.data?.meta?.products
          })
          .catch(error => {
            if (error.response.status === 404) {
              this.isNotFound = true
            } else {
              this.error = true
            }
          })
          .finally(() => {
            this.productsLoading = false
            this.productsLoaded = true
            this.$root.$emit('scrollBeforeEnter')
          })
      },
      setHasFeatureFilterState(value) {
        this.hasFeatureFilter = value
      },
      loadSaleBanner () {
        return ApiSlide.getActiveSlide(this.$axios, { slide_type: 'sale' })
          .then(response => {
            this.saleBanner = response.data.data
          })
          .catch(error => {
            console.error(error)
          })
      },
      triggerFunctionsAfterMounting(products) {
        this.updateMetadataProducts(products)

        if (!products.length) { return }

        this.trackEvent('browse', {
          'type': 'category',
          'name': this.getMetaTitle,
          'products': products.map(x => x.objectID)
        })
      },
      subscribeSetSearchReload () {
        this.unsubscribeMutationListener =
          this.$store.subscribe(mutation => {
            if (mutation.type === 'globalData/setSearchReload' &&
              mutation.payload === true) {
              this.$store.dispatch('productData/clearCurrentProduct')
              this.$store.dispatch('globalData/setSearchReload', false)
              this.loadProducts(this.$route.fullPath)
            }
          })
      },
      collectionFilterDefaultValues (values, filterName) {
        return values.map(value => this.collectionFilterDefaultValue(value, filterName))
      },
      collectionFilterDefaultValue (value, filterName) {
        return `"properties.filter_${filterName}":"${value}"`
      }
    }
  }
</script>
<style lang="scss" scoped>
  @import '@/assets/stylesheets/product-listing';
  @import '@/../shared/assets/stylesheets/animations/fade';

</style>
